/** @format */

import {
	AfterViewInit,
	Component,
	OnDestroy,
	OnInit,
	Input
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import {
	Review,
	selectProductReviewsMultimedia,
	ProductReviewSlider,
	selectProductReviewSlider,
	AppProductReviewState,
	ReviewMedia,
	InvalidResponse,
	ProductReviewSliderType,
	ProductReviewSliderPosition,
	addSliderState,
	UtilityReviewService
} from '@product-reviews/data-access';
import { MediaSourceEnum, ObjectFitEnum } from '../models';

@Component({
    selector: 'product-reviews-review-section',
    templateUrl: './review-section.component.html',
    styleUrls: ['./review-section.component.scss'],
    standalone: false
})
export class ReviewSectionComponent implements AfterViewInit, OnDestroy, OnInit {
	@Input() hasNewReviewsEnhancement = false;

	review: Review;
	currentReviewMedias: ReviewMedia[] = [];
	photoSelected: string;
	sliderPositon = 0;
	starsOptions = {
		width: '16px',
		height: '16px'
	};

	reviewsMultimedia$: Observable<Review[] | InvalidResponse> = this.store.select(
		selectProductReviewsMultimedia
	);
	slider$: Observable<ProductReviewSlider> = this.store.select(selectProductReviewSlider);
	private UNSUBSCRIBE$ = new Subject<void>();

	isButtonRight = true;
	isButtonLeft = true;
	media: ReviewMedia;
	position: ProductReviewSliderPosition;
	safeUrl: SafeResourceUrl;
	mediaSource = MediaSourceEnum.SECTION;
	imageFit = ObjectFitEnum.CONTAIN;

	constructor(
		private store: Store<AppProductReviewState>,
		private utilityReviewService: UtilityReviewService,
		public sanitizer: DomSanitizer
	) {}

	ngOnInit() {
		this.fetchReviews();
	}

	ngAfterViewInit() {
		this.slider$
			.pipe(
				takeUntil(this.UNSUBSCRIBE$),
				filter((slider) => slider.media !== undefined)
			)
			.subscribe((slider: ProductReviewSlider) => {
				this.safeUrl =
					slider.media?.video?.VideoIframeUrl &&
					this.sanitizer.bypassSecurityTrustResourceUrl(
						`${slider.media.video.VideoIframeUrl}?rel=0`
					);
				this.media = slider.media;
				this.review = slider.review;
				this.sliderPositon = slider.position.current;
				this.position = slider.position;
				this.isButtonLeft = this.sliderPositon > 0;
				this.isButtonRight = this.sliderPositon + 1 !== this.currentReviewMedias.length;
				this.photoSelected = this.media.id;
			});
	}

	ngOnDestroy() {
		this.UNSUBSCRIBE$.next();
		this.UNSUBSCRIBE$.complete();
	}

	returnMediaSelected(item: ReviewMedia) {
		this.photoSelected = item.id;
		this.media = item;

		this.currentReviewMedias.find((media, index) => {
			if (this.photoSelected === media.id) {
				this.position = {
					current: index,
					next: index + 1,
					previous: index - 1
				};

				return true;
			}
		});

		this.store.dispatch(
			addSliderState({
				slider: {
					media: this.media,
					review: this.review,
					position: this.position
				}
			})
		);
	}

	buttonDirection(direction: 'Left' | 'Right' | 'None') {
		let currentPosition: ProductReviewSliderPosition = this.position;
		this.syncReviews();

		if (direction === ProductReviewSliderType.LEFT) {
			this.sliderPositon -= 1;
			currentPosition = {
				previous: this.position.previous - 1 > 0 ? this.position.previous - 1 : 0,
				current: this.position.current - 1,
				next:
					this.currentReviewMedias.length > this.position.next - 1
						? this.position.next - 1
						: this.currentReviewMedias.length
			};
		}

		if (direction === ProductReviewSliderType.RIGHT) {
			this.sliderPositon += 1;
			currentPosition = {
				previous: this.position.previous + 1 > 0 ? this.position.previous + 1 : 0,
				current: this.position.current + 1,
				next:
					this.currentReviewMedias.length > this.position.next + 1
						? this.position.next + 1
						: this.currentReviewMedias.length
			};
		}

		if (direction !== ProductReviewSliderType.NONE) {
			this.media = this.currentReviewMedias[this.sliderPositon];
			this.reviewsMultimedia$.pipe(take(1)).subscribe((media: Review[]) => {
				this.review = media.find(
					(review: Review) =>
						review.Id === this.currentReviewMedias[this.sliderPositon].reviewId
				);

				this.store.dispatch(
					addSliderState({
						slider: {
							media: this.currentReviewMedias[this.sliderPositon],
							review: this.review,
							position: currentPosition
						}
					})
				);
			});
		}
	}

	protected trackByBadge(_: number, badgeType: string): string {
		return badgeType;
	}

	fetchReviews() {
		this.reviewsMultimedia$
			.pipe(takeUntil(this.UNSUBSCRIBE$))
			.subscribe((reviewsMultimedia: Review[]) => {
				this.currentReviewMedias =
					this.utilityReviewService.transformReviewMedia(reviewsMultimedia);
			});
	}

	syncReviews() {
		this.reviewsMultimedia$.pipe(take(1)).subscribe((reviewsMultimedia: Review[]) => {
			this.currentReviewMedias =
				this.utilityReviewService.transformReviewMedia(reviewsMultimedia);
		});
	}
}
